import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/SearchInput";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface FilterTab {
  value: string;
  label: string;

  count?: number;
}

export function FilterBar({
  tabs,
  value,
  onValueChange,
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  className,
  children,
}: {
  tabs: FilterTab[];
  value: string;
  onValueChange: (value: string) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn("mb-4 flex flex-wrap items-center gap-3", className)}>
      <Tabs value={value} onValueChange={onValueChange} className="max-w-full">
        <TabsList className="max-w-full justify-start overflow-x-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
              {t.count !== undefined && t.count > 0 && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "ml-2 px-1.5 py-0 text-[10px] tabular-nums",
                    value !== t.value &&
                      "border-transparent bg-sky-100 text-sky-700 hover:bg-sky-100 dark:bg-sky-500/15 dark:text-sky-300",
                  )}
                >
                  {t.count}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {(children || onSearchChange) && (
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {children}
          {onSearchChange && (
            <SearchInput
              value={search ?? ""}
              onSearch={onSearchChange}
              placeholder={searchPlaceholder}
            />
          )}
        </div>
      )}
    </div>
  );
}
